import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { DocumentPageSchema } from '@avisoma/shared';
import StatusIndicator from '../../components/StatusIndicator';
import Tag from '../../components/Tag';
import FileSize from '../../components/FileSize';
import { CategoryWithSuggestionApproval } from '../../components/CategoryWithSuggestionApproval';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import CopyButton from '../../components/CopyButton';
import { formatDateTime } from '../../lib/dateFormat';
import backend from '../../services/backend';
import {
  shouldOfferEntityOverride,
  type EntityOverrideOffer,
} from './shouldOfferEntityOverride';

interface PageInfoCardProps {
  page: DocumentPageSchema;
  onCategoryUpdated: () => void;
}

export function PageInfoCard({ page, onCategoryUpdated }: PageInfoCardProps) {
  const queryClient = useQueryClient();
  const [overrideOffer, setOverrideOffer] = useState<EntityOverrideOffer | null>(null);
  const [overridePending, setOverridePending] = useState(false);
  const [offerForPageId, setOfferForPageId] = useState(page.id);
  const pageIdRef = useRef(page.id);
  pageIdRef.current = page.id;
  // The route re-renders PageInfoCard instead of remounting on :id change
  // (same as DocumentPageContent). Drop a prompt that belongs to the previous page.
  if (page.id !== offerForPageId) {
    setOfferForPageId(page.id);
    setOverrideOffer(null);
    setOverridePending(false);
  }
  // A segmented parent keeps no category of its own — saving one here is
  // redirected server-side onto every section child, so surface the count.
  const segmentation = page.processing?.segmentation;
  const segmentedChildCount =
    segmentation?.status === 'segmented' ? (segmentation.result?.segmentPageIds ?? []).length : undefined;

  const savePageCategory = async (category: string | null) => {
    const savedPageId = page.id;
    await backend.updateDocumentPage(savedPageId, { category });
    onCategoryUpdated();
    if (category === null) return;
    try {
      const list = (await backend.listReconstructedDocumentsByPage(savedPageId, 1, 2)).data;
      const itemId = list.items.length === 1 ? list.items[0].id : null;
      const entity =
        itemId === null ? null : (await backend.getReconstructedDocument(itemId)).data.item;
      const offer = shouldOfferEntityOverride({
        savedCategory: category,
        reconstructedList: list,
        entity,
      });
      if (offer !== null && pageIdRef.current === savedPageId) setOverrideOffer(offer);
    } catch {
      // The page badge already saved. Skip the extra prompt on a lookup error.
    }
  };

  const confirmEntityOverride = async () => {
    if (overrideOffer === null) return;
    setOverridePending(true);
    const entityId = overrideOffer.entityId;
    const category = overrideOffer.category;
    const closeDialog = await (async (): Promise<boolean> => {
      try {
        await backend.patchReconstructedDocument(entityId, {
          userSuggestedCategory: category,
        });
        return true;
      } catch (error) {
        // 409: the category row was saved and the enqueue failed.
        // Any other error leaves the prompt open for a real retry.
        const status = (error as { response?: { status?: number } }).response?.status;
        return status === 409;
      }
    })();
    // Close before the refetch. A refetch await would re-enable Override
    // while the prompt is still open, and a second click would return 200
    // without starting extraction.
    if (closeDialog) setOverrideOffer(null);
    setOverridePending(false);
    if (!closeDialog) return;
    await queryClient.invalidateQueries({
      queryKey: ['reconstructed-document', entityId],
    });
    await queryClient.invalidateQueries({
      queryKey: ['reconstructed-documents', page.id],
    });
  };

  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6 border-b border-gray-200 dark:border-zinc-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Page Information</h2>
      </div>
      <div className="px-4 py-5 sm:p-6 space-y-4">
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Page ID</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100 font-mono flex items-center gap-2 min-w-0">
            <span className="truncate" title={page.id}>
              {page.id}
            </span>
            <CopyButton className="shrink-0" text={page.id} />
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Category</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">
            <CategoryWithSuggestionApproval
              category={page.category}
              classification={page.processing?.classification?.result}
              segmentedChildCount={segmentedChildCount}
              onSave={savePageCategory}
            />
            <ConfirmDialog
              open={overrideOffer !== null}
              title="Also override this entity's category?"
              message="This page is the only page of this entity. The page label does not change extraction. An entity override does."
              confirmLabel="Override"
              pendingLabel="Saving…"
              color="indigo"
              isPending={overridePending}
              onConfirm={() => void confirmEntityOverride()}
              onClose={() => {
                if (!overridePending) setOverrideOffer(null);
              }}
            />
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Status</dt>
          <dd className="mt-1 flex items-center gap-2 text-sm text-gray-900 dark:text-zinc-100">
            <StatusIndicator status={page.status} />
            {page.status}
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Size</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">
            <FileSize bytes={page.size} />
          </dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Mime Type</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100 font-mono">{page.mimeType}</dd>
        </div>

        {page.tags && page.tags.length > 0 && (
          <div>
            <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Tags</dt>
            <dd className="mt-2 flex flex-wrap gap-2">
              {page.tags.map((tag) => (
                <Tag key={tag} tag={tag} />
              ))}
            </dd>
          </div>
        )}

        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Created</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">{formatDateTime(page.createdAt)}</dd>
        </div>

        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Updated</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">{formatDateTime(page.updatedAt)}</dd>
        </div>
      </div>
    </div>
  );
}
